'use client';

import { useContext } from 'react';
import {
  Navbar,
  NavbarToggler,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
} from 'reactstrap';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { toast } from 'react-toastify';

import {
  SettingsIcon, OurPMIcon, IntegrationsIcon,
  LogoutIcon, LogoIcon,
} from 'src/assets/images/svg-icons';
import { logout } from 'src/services/auth';
import GlobalSearch from 'src/components/global-search/global-search';
import { clearAccessToken } from 'src/helpers/token';
import { clearDatadogUser } from 'src/lib/datadog';
import { handleErrorMessages } from 'src/helpers/utils';
import { AuthContext } from 'src/context/auth-context/auth-context';
import useFeatureFlags from 'src/hooks/use-feature-flags';

const Header = () => {
  const [userDetails] = useContext(AuthContext);
  const { customerRentvineSelfServe: isRentvineSelfServeEnabled } = useFeatureFlags();

  const sidemenuToggle = () => {
    document.getElementById('sidebarNav')?.classList.toggle('open');
  };
  const router = useRouter(); const handleLogout = () => {
    logout().catch((error: unknown) => {
      const err = error as Record<string, Record<string, unknown>>;
      handleErrorMessages({
        errors: err?.response?.data || err?.message || err?.errors,
      }, (msg: string) => toast(msg, { type: 'error' }));
    }).finally(() => {
      // Drop the RUM attribution before leaving, so views emitted between here
      // and the redirect are not billed to the property manager who just signed out.
      clearDatadogUser();
      clearAccessToken();
      router.push('/auth');
    });
  };
  return (
    <Navbar expand="lg" className="admin-navbar-head">
      <div className="nav-wrapper">
        <div className="logo-outer-xs">
          <Link href="/dashboard" className="sidebar-logo-outer">
            <LogoIcon />
            <h5>Customer Dashboard</h5>
          </Link>
        </div>
        <div className="d-flex ml-auto">
          <ul className="d-flex align-items-center header-dropdown-outer">
            <UncontrolledDropdown nav inNavbar className="d-none d-lg-block">
              <div className="header-button-section">
                <div
                  className="header-button-section--inner excel-button-wrapper"
                  id="header-portal-container"
                />
              </div>
            </UncontrolledDropdown>
            {/* <UncontrolledDropdown nav inNavbar className="d-none d-lg-block">
                <DropdownToggle nav className="header-notification">
                  <div className="header-notification--icon unread">
                    <NotificationsIcon />
                  </div>
                </DropdownToggle>
                <DropdownMenu right>
                  <div className="notifications-outer">
                    <div className="notifications--item">
                      <div className="notifications--item-type invoice">60</div>
                      <div className="notifications--item-data">
                        Invoice
                        <Link to="#">#ABC3T6GH</Link>
                        is 60 days past due.
                      </div>
                      <div className="notifications--item-timestamp">1d ago</div>
                    </div>
                    <div className="notifications--item">
                      <div className="notifications--item-type invoice">30</div>
                      <div className="notifications--item-data">
                        Invoice
                        <Link to="#">#ABC3T6GH</Link>
                        is 30 days past due.
                      </div>
                      <div className="notifications--item-timestamp">4w ago</div>
                    </div>
                    <div className="notifications--item">
                      <div className="notifications--item-type custom-message">
                      <ChatBubbleIcon /></div>
                      <div className="notifications--item-data">
                        Custom message from admin. Custom message from admin.
                        message from admin.Custom message…
                        <Link to="#">
                          Read More
                        </Link>
                      </div>
                      <div className="notifications--item-timestamp">2mos ago</div>
                    </div>
                    <div className="notifications--view-all">
                      <Link to="/notifications">View All Notifications</Link>
                    </div>
                  </div>
                </DropdownMenu>
              </UncontrolledDropdown> */}
            <GlobalSearch />
            <UncontrolledDropdown nav inNavbar>
              <DropdownToggle nav caret className="header-profile">
                <div className="profile-name">
                  <span className="d-none d-lg-block">
                    {userDetails?.first_name}
                    {' '}
                    {userDetails?.last_name}
                  </span>
                  <span className="d-xs-block d-sm-block d-md-block d-lg-none">
                    {userDetails?.first_name?.charAt(0).toUpperCase()}
                    {userDetails?.last_name?.charAt(0).toUpperCase()}
                  </span>
                </div>
              </DropdownToggle>
              <DropdownMenu end>
                <div className="user-info">
                  <div className="user-info--icon">
                    {userDetails?.profile_pic_url
                      ? (
                        <img
                          src={userDetails.profile_pic_url as string}
                          alt={`${userDetails.first_name} ${userDetails.last_name}`}
                        />
                      )
                      : (
                        <span>
                          {userDetails?.first_name?.charAt(0).toUpperCase()}
                          {userDetails?.last_name?.charAt(0).toUpperCase()}
                        </span>
                      )}
                  </div>
                  <div className="user-info--name_email">
                    <h4>
                      {userDetails?.first_name}
                      {' '}
                      {userDetails?.last_name}
                    </h4>
                    <h5>{userDetails?.email}</h5>
                  </div>
                </div>
                <div className="profile-menu--item">
                  <Link href="/my-profile">
                    <SettingsIcon />
                    Settings
                  </Link>
                </div>
                {
                    (userDetails?.property_manager_type !== 'employee') && (
                      <div className="profile-menu--item">
                        <Link href="/property-managers">
                          <OurPMIcon />
                          Our Property Managers
                        </Link>
                      </div>
                    )
                  }
                {
                    // PYR-529 — customer-side Rentvine self-serve rolls out behind
                    // its own flag, staged independently from the admin-side one.
                    (userDetails?.property_manager_type !== 'employee') && isRentvineSelfServeEnabled && (
                      <div className="profile-menu--item">
                        <Link href="/integrations">
                          <IntegrationsIcon />
                          Integrations
                        </Link>
                      </div>
                    )
                  }
                <div className="profile-menu--item">
                  <button type="button" className="logout-btn logout-txt" onClick={handleLogout}>
                    <LogoutIcon />
                    Logout
                  </button>
                </div>
              </DropdownMenu>
            </UncontrolledDropdown>
          </ul>
          <NavbarToggler data-toggle="offcanvas" onClick={sidemenuToggle} />
        </div>
      </div>
    </Navbar>
  );
};

export default Header;
